import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosRefresh } from 'react-icons/io';
import { PiNotePencil, PiArrowSquareOut } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import { selectEventsTableFilters, setEventsTableFilters } from '../../redux/slices/tableStatesSlice';
import PermissionChecker from '../UI/PermissionChecker/PermissionChecker';
import { PERMISSIONS } from '../../shared/permission';
import { eventService } from '../../services/event.service';
import TableGrid from '../UI/Table/TableGrid/TableGrid';
import IconButton from '../UI/IconButton/IconButton';
import Button from '../UI/Button/Button';
import ColoredText from '../UI/Table/ColoredText/ColoredText';
import EventCreateForm from './EventCreateForm/EventCreateForm';
import EventEditForm from './EventEditForm/EventEditForm';
import styles from './Events.module.css';

const statusColorMap = {
    draft: 'yellow',
    published: 'green',
    archived: 'gray',
};

const getTimeStatus = (row) => {
    if (!row.start_date || !row.end_date) return 'future';
    const now = new Date();
    if (now < new Date(row.start_date)) return 'future';
    if (now > new Date(row.end_date)) return 'past';
    return 'active';
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function Events() {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const eventsTableFilters = useSelector(selectEventsTableFilters);

    const validPageSize = PAGE_SIZE_OPTIONS.includes(eventsTableFilters.pageSize)
        ? eventsTableFilters.pageSize
        : 10;

    const [events, setEvents] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        page: eventsTableFilters.page ?? 0,
        pageSize: validPageSize,
    });
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editEventId, setEditEventId] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);

    const columns = [
        { field: 'id', headerName: '№', width: 60, flex: 0, sortable: false },
        {
            field: 'title',
            headerName: t('events.title_label'),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const ts = getTimeStatus(params.row);
                const isDraft = params.row.status === 'draft';
                return (
                    <div className={styles.titleCell}>
                        <span className={styles.titleText}>{params.value}</span>
                        {!isDraft && (
                            <span className={`${styles.timeBadge} ${styles[`timeBadge_${ts}`]}`}>
                                {t(`events.time_${ts}`)}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'cover_image',
            headerName: t('events.cover'),
            width: 110,
            flex: 0,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                const url = row.cover_image;
                if (!url) return null;
                const src = url.startsWith('data:') || url.startsWith('http') ? url : `data:image/jpeg;base64,${url}`;
                const isPast = getTimeStatus(params.row) === 'past';
                return (
                    <div
                        className={styles.coverCell}
                        onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setCoverPreview({ src, x: rect.left + rect.width / 2, y: rect.top });
                        }}
                        onMouseLeave={() => setCoverPreview(null)}
                    >
                        <img src={src} alt="" className={`${styles.coverThumb} ${isPast ? styles.coverThumbMuted : ''}`} />
                    </div>
                );
            },
        },
        {
            field: 'tags',
            headerName: t('events.tags'),
            width: 160,
            flex: 0,
            sortable: false,
            renderCell: (params) => {
                const tags = params.row.tags ?? params.value;
                if (!tags?.length) return null;
                const text = tags.map((tag) => tag?.name ?? tag?.code ?? tag).join(', ');
                return <span className={styles.tagsText}>{text}</span>;
            },
        },
        {
            field: 'start_date',
            headerName: t('events.start_date_label'),
            width: 130,
            flex: 0,
            sortable: false,
            renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null,
        },
        {
            field: 'end_date',
            headerName: t('events.end_date_label'),
            width: 130,
            flex: 0,
            sortable: false,
            renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null,
        },
        {
            field: 'status',
            headerName: t('events.status_label'),
            width: 140,
            flex: 0,
            sortable: false,
            renderCell: (params) => (
                <ColoredText
                    value={t(`events.status_${params.value}`)}
                    colorMap={statusColorMap}
                    colorCondition={params.value}
                />
            ),
        },
        {
            field: 'actions',
            headerName: t('events.actions'),
            width: 90,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', height: '100%' }}>
                    <IconButton
                        variant="ghost"
                        size="small"
                        onClick={() => setEditEventId(params.id)}
                        title={t('events.edit')}
                    >
                        <PiNotePencil />
                    </IconButton>
                </div>
            ),
        },
    ];

    useEffect(() => {
        let alive = true;
        const load = async () => {
            setIsLoading(true);
            try {
                const data = await eventService.getEvents({
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                });
                if (alive) {
                    setEvents(data?.rows ?? []);
                    setTotal(data?.total ?? 0);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (alive) setIsLoading(false);
            }
        };
        load();
        return () => { alive = false; };
    }, [paginationModel.page, paginationModel.pageSize, refreshKey]);

    return (
        <PermissionChecker permissions={[PERMISSIONS.EVENTS.VIEW]}>
            <div className={styles.container}>
                <div className={styles.tableWrapper}>
                    <div className={styles.headerContainer}>
                        <IconButton onClick={() => { setPaginationModel((m) => ({ ...m, page: 0 })); setRefreshKey((k) => k + 1); }} variant="secondary">
                            <IoIosRefresh />
                        </IconButton>
                        <div className={styles.headerRight}>
                            <Button
                                variant="secondary"
                                onClick={() => window.open('/events', '_blank')}
                                className={styles.viewPublicButton}
                            >
                                <PiArrowSquareOut />
                                {t('events.view_public')}
                            </Button>
                            <PermissionChecker permissions={[PERMISSIONS.EVENTS.CREATE]}>
                                <Button
                                    variant="primary"
                                    onClick={() => setIsCreateOpen(true)}
                                    className={styles.createButton}
                                >
                                    {t('events.create')}
                                </Button>
                            </PermissionChecker>
                        </div>
                    </div>
                    <TableGrid
                        noRowsText={t('events.no_active_events')}
                        columns={columns}
                        rows={events}
                        getRowClassName={(params) => `row-${getTimeStatus(params.row)}`}
                        loading={isLoading}
                        rowCount={total}
                        paginationMode="server"
                        paginationModel={paginationModel}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onPaginationModelChange={(model) => {
                            setPaginationModel(model);
                            dispatch(setEventsTableFilters({
                                ...eventsTableFilters,
                                page: model.page,
                                pageSize: model.pageSize,
                            }));
                        }}
                        rowHeight={52}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableColumnSort
                    />
                </div>
            </div>

            {coverPreview && (
                <div
                    className={styles.coverPreviewPortal}
                    style={{ left: coverPreview.x, top: coverPreview.y - 8 }}
                >
                    <img src={coverPreview.src} alt="" />
                </div>
            )}

            <EventCreateForm
                isOpen={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                onSuccess={() => { setPaginationModel((m) => ({ ...m, page: 0 })); setRefreshKey((k) => k + 1); }}
            />
            <EventEditForm
                isOpen={!!editEventId}
                eventId={editEventId}
                onClose={() => setEditEventId(null)}
                onSuccess={() => { setPaginationModel((m) => ({ ...m, page: 0 })); setRefreshKey((k) => k + 1); }}
            />
        </PermissionChecker>
    );
}

export default Events;
